// For Gitea EE
import {createSortable} from '../modules/sortable.ts';
import {POST} from '../modules/fetch.ts';
import {showErrorToast} from '../modules/toast.ts';
import {createElementFromHTML, queryElems, toggleElem} from '../utils/dom.ts';
import {html, htmlRaw} from '../utils/html.ts';
import {svg} from '../svg.ts';

// rebuild the one-line summary of a collapsed row from its current input values
function refreshPathRuleSummary(row: HTMLElement) {
  const summary = row.querySelector<HTMLElement>('.path-rule-summary-text')!;
  const name = row.querySelector<HTMLInputElement>('input[name="path_rule_name"]')!.value.trim();
  const patterns = row.querySelector<HTMLInputElement>('input[name="path_rule_file_patterns"]')!.value.trim();
  const modeSelect = row.querySelector<HTMLSelectElement>('select[name="path_rule_push_mode"]')!;
  const modeLabel = modeSelect.selectedOptions[0].textContent;

  summary.textContent = '';
  if (name) {
    const nameElem = document.createElement('b');
    nameElem.textContent = name;
    summary.append(nameElem, ' ');
  }
  if (patterns) {
    const patternsElem = document.createElement('code');
    patternsElem.textContent = patterns;
    summary.append(patternsElem, ' ');
  }
  summary.append(`· ${modeLabel}`);
}

function setPathRuleCollapsed(row: HTMLElement, collapsed: boolean) {
  if (collapsed) refreshPathRuleSummary(row);
  toggleElem(row.querySelector('.path-rule-body')!, !collapsed);
  toggleElem(row.querySelector('.path-rule-chevron-collapsed')!, collapsed);
  toggleElem(row.querySelector('.path-rule-chevron-expanded')!, !collapsed);
}

function isPathRuleCollapsed(row: HTMLElement): boolean {
  return row.querySelector('.path-rule-body')!.classList.contains('tw-hidden');
}

function initPathRuleRow(row: HTMLElement) {
  row.querySelector('.path-rule-remove')!.addEventListener('click', () => row.remove());

  row.querySelector('.path-rule-summary')!.addEventListener('click', () => {
    setPathRuleCollapsed(row, !isPathRuleCollapsed(row));
  });

  const modeSelect = row.querySelector<HTMLSelectElement>('select[name="path_rule_push_mode"]')!;
  modeSelect.addEventListener('change', () => {
    toggleElem(row.querySelector('.path-rule-whitelist-box')!, modeSelect.value === 'whitelist');
  });

  // the summary is owned by JS (single source of the format):
  // fill it for rows that start collapsed (server-rendered ones); expanded rows get it on first collapse
  if (isPathRuleCollapsed(row)) refreshPathRuleSummary(row);
}

export function initEERepoSettingsPathRulesForm() {
  const pathRulesList = document.querySelector<HTMLElement>('#path-rules-edit-list');
  if (!pathRulesList) return;

  createSortable(pathRulesList, {
    handle: '.drag-handle',
    animation: 150,

    // collapse every row while dragging so the drop target is always visible
    onStart: () => {
      queryElems(pathRulesList, '.path-rule-row', (row) => setPathRuleCollapsed(row, true));
    },
  });

  queryElems(pathRulesList, '.path-rule-row', initPathRuleRow);

  const rowTemplate = document.querySelector<HTMLTemplateElement>('#path-rule-row-template')!;
  document.querySelector('#path-rule-add')!.addEventListener('click', () => {
    const row = rowTemplate.content.querySelector<HTMLElement>('.path-rule-row')!.cloneNode(true) as HTMLElement;
    pathRulesList.append(row);
    initPathRuleRow(row);
  });

  // when pushing is disabled at the branch level, path rules only apply to merging pull requests. Show the hint.
  const pushDisabledHint = document.querySelector('#path-rules-push-disabled-hint');
  if (pushDisabledHint) {
    queryElems<HTMLInputElement>(document, 'input[name="enable_push"]', (radio) => {
      radio.addEventListener('change', () => {
        toggleElem(pushDisabledHint, radio.value === 'none');
      });
    });
  }
}

type EvaluateDecision = {path: string, allowed: boolean, decided_by: string, rule_index: number, rule_name: string};
type EvaluateResult = {rule_name: string, inherited_from_org: boolean, decisions: EvaluateDecision[]};

function decisionLabel(labelsElem: HTMLElement, decision: EvaluateDecision): string {
  const layer = labelsElem.getAttribute(`data-locale-${decision.decided_by.replaceAll('_', '-')}`)!;
  if (decision.decided_by === 'path_rule') {
    return `${layer} ${decision.rule_name || `#${decision.rule_index + 1}`}`;
  }
  return layer;
}

// only two icons are ever rendered, and svg() with a class is not cached
const allowedIcon = svg('octicon-check', 16, 'tw-text-green');
const deniedIcon = svg('octicon-x', 16, 'tw-text-red');

function renderEvaluateResult(resultElem: HTMLElement, result: EvaluateResult) {
  const labelAttr = result.inherited_from_org ? 'data-locale-applied-rule-inherited' : 'data-locale-applied-rule';
  const [labelPrefix, labelSuffix = ''] = resultElem.getAttribute(labelAttr)!.split('%s');
  const rows = result.decisions.map((decision) => html`
    <div class="flex-item tw-items-center tw-gap-2">
        ${htmlRaw(decision.allowed ? allowedIcon : deniedIcon)}
        <code>${decision.path}</code>
        <span class="text light-2">${decisionLabel(resultElem, decision)}</span>
      </div>
  `);
  resultElem.replaceChildren(createElementFromHTML(html`
    <div>
        <p class="help tw-block tw-pb-0">${labelPrefix}<b>${result.rule_name}</b>${labelSuffix}</p>
        <div class="flex-list">${htmlRaw(rows.join(''))}</div>
      </div>
  `));
}

// The preview on the branch protection rule list page, see path_rules_evaluate.tmpl.
export function initEERepoSettingsPathRulesEvaluate() {
  const runButton = document.querySelector<HTMLButtonElement>('#path-rules-evaluate-run');
  if (!runButton) return; // only rendered with a valid license

  const resultElem = document.querySelector<HTMLElement>('#path-rules-evaluate-result')!;
  const pathsElem = document.querySelector<HTMLTextAreaElement>('#path-rules-evaluate-paths')!;
  const branchElem = document.querySelector<HTMLInputElement>('#path-rules-evaluate-branch')!;
  const userElem = document.querySelector<HTMLInputElement>('#path-rules-evaluate-user')!;

  runButton.addEventListener('click', async () => {
    const paths = pathsElem.value.split('\n').map((p) => p.trim()).filter(Boolean);
    if (!paths.length || !userElem.value || !branchElem.value) return;

    resultElem.replaceChildren();
    runButton.disabled = true;
    try {
      const response = await POST(runButton.getAttribute('data-url')!, {
        data: {branch_name: branchElem.value, username: userElem.value, paths},
      });
      const data = await response.json();
      if (!response.ok) {
        showErrorToast(data.errorMessage ?? response.statusText);
        return;
      }
      renderEvaluateResult(resultElem, data);
    } catch (err) {
      showErrorToast(String(err));
    } finally {
      runButton.disabled = false;
    }
  });
}
